const BASE = `${process.env.REACT_APP_API_BASE_URL}/api/puppeteer-resume`;

const getAuthHeaders = (json = true) => {
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const handleUnauth = (res) => {
  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  return res;
};

// ── Helper: always send description as string to API ──────────────────────
const normalizeExperiences = (experiences = []) =>
  experiences.map(exp => ({
    ...exp,
    description: Array.isArray(exp.description)
      ? exp.description.filter(Boolean).join('\n')
      : exp.description || ''
  }));

const normalizeDto = (resumeData) => ({
  ...resumeData,
  experiences: normalizeExperiences(resumeData.experiences),
});

/* 1. Preview — returns HTML string */
export const getPreviewHtml = async (resumeData) => {
  const res = await fetch(`${BASE}/preview`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(normalizeDto(resumeData)),
  });
  handleUnauth(res);
  if (!res.ok) throw new Error('Preview failed');
  return res.text();
};

/* 2. Download PDF progress — SSE via POST ReadableStream */
export const downloadPdfWithProgress = async (resumeData, onProgress, onError) => {
  const res = await fetch(`${BASE}/download-pdf-progress`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(normalizeDto(resumeData)),
  });
  handleUnauth(res);
  if (!res.ok) {
    onError?.('Failed to start PDF generation');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop();

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          onProgress?.(data);
        } catch {}
      }
      if (line.startsWith('event: error')) {
        const errIdx = lines.indexOf(line);
        const nextLine = lines[errIdx + 1];
        if (nextLine?.startsWith('data: ')) {
          try {
            const errData = JSON.parse(nextLine.slice(6));
            onError?.(errData.error || 'PDF generation error');
          } catch {
            onError?.('PDF generation error');
          }
        }
      }
    }
  }
};

/* 3. Download PDF — returns blob, triggers browser download */
export const downloadPdf = async (resumeData) => {
  const res = await fetch(`${BASE}/download-pdf`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(normalizeDto(resumeData)),
  });
  handleUnauth(res);
  if (!res.ok) throw new Error('Download failed');

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${resumeData.firstName || 'Resume'}_${resumeData.surname || ''}_Resume.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/* 4. Parse uploaded resume — multipart/form-data */
export const parseUploadedResume = async (file) => {
  const formData = new FormData();
  formData.append('resume', file);

  const res = await fetch(`${BASE}/parse-upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: formData,
  });
  handleUnauth(res);
  if (!res.ok) throw new Error('Upload parsing failed');
  return res.json();
};

/* 5. My resumes list */
export const getMyResumes = async () => {
  const res = await fetch(`${BASE}/my-resumes`, {
    headers: getAuthHeaders(false),
  });
  handleUnauth(res);
  if (!res.ok) throw new Error('Failed to fetch resumes');
  return res.json();
};

/* 6. Get single resume by ID */
export const getResumeById = async (resumeId) => {
  const res = await fetch(`${BASE}/${resumeId}`, {
    headers: getAuthHeaders(false),
  });
  handleUnauth(res);
  if (!res.ok) throw new Error('Failed to fetch resume');
  return res.json();
};